import React, { useEffect, useState, useRef } from 'react';
import { ClockIcon, MapPinIcon, TruckIcon, XIcon, AlertCircleIcon, CheckCircleIcon, ArrowRightIcon, PlusIcon, MessageSquareIcon, ArrowLeftIcon, PhoneIcon, FileTextIcon, InfoIcon, CameraIcon, UploadIcon, AlertTriangleIcon, TableIcon, CheckIcon } from 'lucide-react';
import { JobCompletionModal } from './JobCompletionModal';
import { JobMessageButton } from '../../messaging/JobMessageButton';
interface JobTrackingScreenProps {
  job: any;
  onClose: () => void;
  onComplete: (jobId: string) => void;
}
interface Load {
  id: number;
  loadInTime: string;
  loadOutTime: string;
  dumpInTime: string;
  dumpOutTime: string;
  ticket: string;
  tons: number;
  scaleTicketInfo?: {
    ticketNumber: string;
    tons: number;
    imageUrl?: string;
  };
  geoPunchDetails?: {
    loadArrival: {
      time: string;
      coordinates?: {
        lat: number;
        lng: number;
      };
    };
    loadDeparture: {
      time: string;
      coordinates?: {
        lat: number;
        lng: number;
      };
    };
    dumpArrival: {
      time: string;
      coordinates?: {
        lat: number;
        lng: number;
      };
    };
    dumpDeparture: {
      time: string;
      coordinates?: {
        lat: number;
        lng: number;
      };
    };
  };
}
interface Comment {
  id: number;
  text: string;
  timestamp: string;
}
export const JobTrackingScreen = ({
  job,
  onClose,
  onComplete
}: JobTrackingScreenProps) => {
  const [totalLoads, setTotalLoads] = useState(0);
  const [totalTonnage, setTotalTonnage] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [startTime, setStartTime] = useState('');
  const [loads, setLoads] = useState<Load[]>([]);
  const [currentLoad, setCurrentLoad] = useState<Partial<Load> | null>(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [showGeoPunchModal, setShowGeoPunchModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{
    lat: number | null;
    lng: number | null;
  }>({
    lat: null,
    lng: null
  });
  // Scale ticket state
  const [showScaleTicketModal, setShowScaleTicketModal] = useState(false);
  const [scaleTicketNumber, setScaleTicketNumber] = useState('');
  const [scaleTonnage, setScaleTonnage] = useState('');
  const [scaleTicketImage, setScaleTicketImage] = useState<string | null>(null);
  const [useAverageTonnage, setUseAverageTonnage] = useState(false);
  const [averageTonnage, setAverageTonnage] = useState('30');
  const [showAverageTonnageInput, setShowAverageTonnageInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Comments state
  const [comments, setComments] = useState<Comment[]>([]);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  // Punch statuses
  const [loadArrivalPunched, setLoadArrivalPunched] = useState(false);
  const [loadDeparturePunched, setLoadDeparturePunched] = useState(false);
  const [dumpArrivalPunched, setDumpArrivalPunched] = useState(false);
  const [dumpDeparturePunched, setDumpDeparturePunched] = useState(false);
  // Show finalize button
  const [showFinalizeButton, setShowFinalizeButton] = useState(false);
  // Job completion modal state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  // Report Problem state
  const [showReportProblemModal, setShowReportProblemModal] = useState(false);
  const [problemType, setProblemType] = useState<'late' | 'breakdown' | 'emergency' | ''>('');
  const [problemComment, setProblemComment] = useState('');
  const [notifyRecipients, setNotifyRecipients] = useState({
    trucking: true,
    broker: false,
    foreman: true,
    all: false
  });
  // Add a function to handle deleting a load
  const handleDeleteLoad = (loadId: number) => {
    // Find the load to delete
    const loadToDelete = loads.find(load => load.id === loadId);
    // Only allow deletion of manually added loads
    if (!loadToDelete || !loadToDelete.isManualEntry) {
      return;
    }
    // Confirm deletion
    if (confirm('Are you sure you want to delete this manually added load?')) {
      // Update the loads state
      setLoads(prevLoads => prevLoads.filter(load => load.id !== loadId));
      // Update total loads count
      setTotalLoads(prevTotal => prevTotal - 1);
      // Update total tonnage
      setTotalTonnage(prevTonnage => prevTonnage - loadToDelete.tons);
    }
  };
  // Initialize when component mounts
  useEffect(() => {
    // Start tracking hours when first load arrives
    if (startTime) {
      const interval = setInterval(() => {
        const startTimeDate = new Date();
        startTimeDate.setHours(parseInt(startTime.split(':')[0]));
        startTimeDate.setMinutes(parseInt(startTime.split(':')[1]));
        const now = new Date();
        const diffMs = now.getTime() - startTimeDate.getTime();
        const diffHrs = diffMs / (1000 * 60 * 60);
        setTotalHours(parseFloat(diffHrs.toFixed(2)));
      }, 60000); // Update every minute
      // Initial calculation
      setTotalHours(0);
      return () => clearInterval(interval);
    }
  }, [startTime]);
  const [showManualLoadModal, setShowManualLoadModal] = useState(false);
  const [manualLoad, setManualLoad] = useState({
    ticketNumber: '',
    tons: '',
    loadInTime: '',
    loadOutTime: '',
    dumpInTime: '',
    dumpOutTime: '',
    scaleTicketImage: null as string | null
  });
  const handleManualLoadChange = (field: string, value: string) => {
    setManualLoad(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleManualScaleTicketImage = (imageUrl: string) => {
    setManualLoad(prev => ({
      ...prev,
      scaleTicketImage: imageUrl
    }));
  };
  const handleManualLoadSubmit = () => {
    // Validate that at least the time fields are filled
    if (!manualLoad.loadInTime || !manualLoad.loadOutTime || !manualLoad.dumpInTime || !manualLoad.dumpOutTime) {
      alert('Please fill in all time fields');
      return;
    }
    const tonsValue = manualLoad.tons ? parseFloat(manualLoad.tons) : useAverageTonnage ? parseFloat(averageTonnage) : 0;
    const newLoad = {
      id: totalLoads + 1,
      loadInTime: manualLoad.loadInTime,
      loadOutTime: manualLoad.loadOutTime,
      dumpInTime: manualLoad.dumpInTime,
      dumpOutTime: manualLoad.dumpOutTime,
      ticket: manualLoad.ticketNumber || `T-${(totalLoads + 1).toString().padStart(4, '0')}`,
      tons: tonsValue,
      scaleTicketInfo: {
        ticketNumber: manualLoad.ticketNumber || `T-${(totalLoads + 1).toString().padStart(4, '0')}`,
        tons: tonsValue,
        imageUrl: manualLoad.scaleTicketImage || undefined
      },
      isManualEntry: true // Flag to mark this as a manual entry
    };
    setLoads(prev => [...prev, newLoad]);
    setTotalLoads(prev => prev + 1);
    setTotalTonnage(prev => prev + tonsValue);
    // Reset form and close modal
    setManualLoad({
      ticketNumber: '',
      tons: '',
      loadInTime: '',
      loadOutTime: '',
      dumpInTime: '',
      dumpOutTime: '',
      scaleTicketImage: null
    });
    setShowManualLoadModal(false);
  };
  const handleAddLoadManually = () => {
    setShowManualLoadModal(true);
  };
  const handleArriveAtLoad = () => {
    // Check if geo punch is required
    if (job.geoPunchRequired) {
      checkGeolocation(isWithinRange => {
        if (isWithinRange) {
          startLoadTracking();
        } else {
          alert(`You must be within ${job.geoPunchDistance || 150} feet of the job location to start tracking.`);
        }
      });
    } else {
      // No geo punch required, directly start tracking
      startLoadTracking();
    }
  };
  const startLoadTracking = () => {
    // Get current time to start the timecard
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const currentTime = `${hours}:${minutes}`;
    // Set the start time for the timecard
    setStartTime(currentTime);
    // Get current location for geo punch
    getCurrentLocation(location => {
      setLoadArrivalPunched(true);
      setCurrentLoad({
        id: totalLoads + 1,
        loadInTime: formatTime(new Date()),
        geoPunchDetails: {
          loadArrival: {
            time: formatTime(new Date()),
            coordinates: location
          },
          loadDeparture: {
            time: ''
          },
          dumpArrival: {
            time: ''
          },
          dumpDeparture: {
            time: ''
          }
        }
      });
      // Show the finalize button
      setShowFinalizeButton(true);
    });
  };
  const handleLeaveLoad = () => {
    // Get current location for geo punch
    getCurrentLocation(location => {
      if (currentLoad?.geoPunchDetails) {
        currentLoad.geoPunchDetails.loadDeparture = {
          time: formatTime(new Date()),
          coordinates: location
        };
      }
      setLoadDeparturePunched(true);
      setCurrentLoad(prev => ({
        ...prev,
        loadOutTime: formatTime(new Date())
      }));
      // Show scale ticket modal
      setShowScaleTicketModal(true);
    });
  };
  const handleArriveAtDump = () => {
    // Get current location for geo punch
    getCurrentLocation(location => {
      if (currentLoad?.geoPunchDetails) {
        currentLoad.geoPunchDetails.dumpArrival = {
          time: formatTime(new Date()),
          coordinates: location
        };
      }
      setDumpArrivalPunched(true);
      setCurrentLoad(prev => ({
        ...prev,
        dumpInTime: formatTime(new Date())
      }));
    });
  };
  const handleLeaveDump = () => {
    // Get current location for geo punch
    getCurrentLocation(location => {
      if (currentLoad?.geoPunchDetails) {
        currentLoad.geoPunchDetails.dumpDeparture = {
          time: formatTime(new Date()),
          coordinates: location
        };
      }
      setDumpDeparturePunched(true);
      // Get the tonnage value to use - either from scale ticket or average
      const tonsValue = scaleTonnage ? parseFloat(scaleTonnage) : useAverageTonnage ? parseFloat(averageTonnage) : 0;
      setCurrentLoad(prev => ({
        ...prev,
        dumpOutTime: formatTime(new Date()),
        ticket: scaleTicketNumber || `T-${(totalLoads + 1).toString().padStart(4, '0')}`,
        tons: tonsValue // Use the correct tonnage value
      }));
      // Complete the load cycle
      completeLoadCycle();
    });
  };
  const checkGeolocation = (callback: (isWithinRange: boolean) => void) => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      callback(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(position => {
      setCurrentLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
      // Check if within required distance
      if (job.loadLocation?.coordinates) {
        const distance = calculateDistance(position.coords.latitude, position.coords.longitude, job.loadLocation.coordinates.lat, job.loadLocation.coordinates.lng);
        if (distance <= (job.geoPunchDistance || 150)) {
          // Within range
          callback(true);
        } else {
          // Out of range
          alert(`You must be within ${job.geoPunchDistance || 150} feet of the job location. You are currently ${Math.round(distance)} feet away.`);
          callback(false);
        }
      } else {
        // No coordinates available, proceed anyway
        callback(true);
      }
    }, error => {
      alert(`Error getting location: ${error.message}`);
      callback(false);
    });
  };
  const getCurrentLocation = (callback: (location: {
    lat: number;
    lng: number;
  } | undefined) => void) => {
    if (job.geoPunchRequired && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        callback(location);
      }, error => {
        console.error('Error getting location:', error);
        callback(undefined);
      });
    } else {
      callback(undefined);
    }
  };
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceInMeters = R * c;
    // Convert to feet
    return distanceInMeters * 3.28084;
  };
  const completeLoadCycle = () => {
    if (currentLoad) {
      // Get the tonnage value to use - either from scale ticket or average
      const tonsValue = scaleTonnage ? parseFloat(scaleTonnage) : useAverageTonnage ? parseFloat(averageTonnage) : 0;
      const completedLoad = {
        id: currentLoad.id || totalLoads + 1,
        loadInTime: currentLoad.loadInTime || '--',
        loadOutTime: currentLoad.loadOutTime || '--',
        dumpInTime: currentLoad.dumpInTime || '--',
        dumpOutTime: currentLoad.dumpOutTime || '--',
        ticket: currentLoad.ticket || `T-${(totalLoads + 1).toString().padStart(4, '0')}`,
        tons: tonsValue,
        scaleTicketInfo: {
          ticketNumber: scaleTicketNumber || `T-${(totalLoads + 1).toString().padStart(4, '0')}`,
          tons: tonsValue,
          imageUrl: scaleTicketImage || undefined
        },
        geoPunchDetails: currentLoad.geoPunchDetails
      };
      setLoads(prev => [...prev, completedLoad]);
      setTotalLoads(prev => prev + 1);
      setTotalTonnage(prev => prev + tonsValue); // Use the correct tonnage value
      // Reset for next load
      setCurrentLoad(null);
      setLoadArrivalPunched(false);
      setLoadDeparturePunched(false);
      setDumpArrivalPunched(false);
      setDumpDeparturePunched(false);
      setScaleTicketNumber('');
      setScaleTonnage('');
      setScaleTicketImage(null);
      setUseAverageTonnage(false);
      setShowAverageTonnageInput(false);
    }
  };
  const handleFinalizeJob = () => {
    // Show the job completion modal instead of confirmation dialog
    setShowCompletionModal(true);
  };
  const handleCompleteJob = (jobId: string, completionData: any) => {
    // Close the modal
    setShowCompletionModal(false);
    // Prepare the complete job data to pass to the completed jobs
    const completedJobData = {
      id: jobId,
      date: job.date,
      completedDate: new Date().toISOString().split('T')[0],
      customer: job.customer,
      jobName: job.jobName,
      jobNumber: job.jobNumber,
      material: job.material,
      truckType: job.truckType,
      loadLocation: job.loadLocation,
      dumpLocation: job.dumpLocation,
      foreman: {
        name: completionData.foremanVerification.foremanName || job.foreman.name,
        signature: !completionData.foremanVerification.skipped
      },
      stats: {
        loads: totalLoads,
        tonnage: totalTonnage,
        hours: totalHours,
        startTime: startTime,
        endTime: formatTime(new Date()),
        truckHours: completionData.truckHours
      },
      status: 'completed',
      ticketNumber: loads.length > 0 ? loads[0].ticket : 'No Ticket',
      paymentStatus: 'pending',
      loadsDetails: loads,
      comments: comments // Pass all comments to completed jobs
    };
    // Log the completion data (in a real app, this would be sent to your backend)
    console.log('Job completed with data:', completedJobData);
    // Call the original onComplete function
    onComplete(jobId);
  };
  const handleCancelCompletion = () => {
    setShowCompletionModal(false);
  };
  const handleReportProblem = () => {
    setShowReportProblemModal(true);
  };
  const handleCloseReportProblem = () => {
    setShowReportProblemModal(false);
    setProblemType('');
    setProblemComment('');
    setNotifyRecipients({
      trucking: true,
      broker: false,
      foreman: true,
      all: false
    });
  };
  const handleNotifyAll = (checked: boolean) => {
    if (checked) {
      setNotifyRecipients({
        trucking: true,
        broker: true,
        foreman: true,
        all: true
      });
    } else {
      setNotifyRecipients({
        ...notifyRecipients,
        all: false
      });
    }
  };
  const handleSubmitProblemReport = () => {
    if (!problemType) {
      alert('Please select a problem type');
      return;
    }
    // In a real app, this would send the alert to the selected parties
    const recipients = notifyRecipients.all ? 'All parties' : [notifyRecipients.trucking ? 'Trucking Company' : '', notifyRecipients.broker ? 'Broker' : '', notifyRecipients.foreman ? 'Foreman' : ''].filter(Boolean).join(', ');
    const problemTypeText = problemType === 'late' ? 'Running Late' : problemType === 'breakdown' ? 'Truck Down' : 'Emergency';
    console.log(`Reporting problem: ${problemTypeText}`);
    console.log(`Comment: ${problemComment}`);
    console.log(`Notifying: ${recipients}`);
    // Add the problem report as a comment
    const now = new Date();
    const alertComment = {
      id: comments.length + 1,
      text: `ALERT - ${problemTypeText}: ${problemComment}`,
      timestamp: `${formatTime(now)} - ${now.toLocaleDateString()}`
    };
    setComments(prev => [...prev, alertComment]);
    // Show confirmation to user
    alert(`Alert sent to ${recipients}:\n${problemTypeText}\n${problemComment}`);
    // Close the modal
    handleCloseReportProblem();
  };
  const formatTime = (date: Date) => {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };
  // Comment handlers
  const handleOpenCommentsModal = () => {
    setShowCommentsModal(true);
  };
  const handleCloseCommentsModal = () => {
    setShowCommentsModal(false);
    setNewComment('');
  };
  const handleSaveComment = () => {
    if (newComment.trim()) {
      const now = new Date();
      const comment = {
        id: comments.length + 1,
        text: newComment.trim(),
        timestamp: `${formatTime(now)} - ${now.toLocaleDateString()}`
      };
      setComments(prev => [...prev, comment]);
      setNewComment('');
      setShowCommentsModal(false);
      // In a real app, you would save this to your backend
      console.log(`Comment saved: ${comment.text}`);
    }
  };
  // Scale ticket handlers
  const handleTakePhoto = () => {
    // In a real app, this would open the device camera
    alert('Camera would open here to take a photo of the scale ticket');
    // For demo purposes, simulate a successful photo capture
    setTimeout(() => {
      // Simulate AI processing the image
      setScaleTicketImage('https://uploadthingy.s3.us-west-1.amazonaws.com/h3f29uWQBQ3nwdi5Lk87T1/driver_scale_ticket_capture.jpg');
      setScaleTicketNumber('SC-' + Math.floor(10000 + Math.random() * 90000));
      setScaleTonnage((Math.floor(Math.random() * 20) + 25).toString());
      alert('AI has processed the image and extracted: \nTicket #: ' + scaleTicketNumber + '\nTonnage: ' + scaleTonnage + ' tons');
    }, 1500);
  };
  const handleUploadImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // In a real app, you would upload the file to your server
      // and process it with AI to extract the ticket number and tonnage
      // For demo purposes, simulate processing
      const reader = new FileReader();
      reader.onload = () => {
        setScaleTicketImage(reader.result as string);
        // Simulate AI processing
        setTimeout(() => {
          setScaleTicketNumber('SC-' + Math.floor(10000 + Math.random() * 90000));
          setScaleTonnage((Math.floor(Math.random() * 20) + 25).toString());
          alert('AI has processed the image and extracted: \nTicket #: ' + scaleTicketNumber + '\nTonnage: ' + scaleTonnage + ' tons');
        }, 1500);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSetAverageTonnage = () => {
    setShowAverageTonnageInput(true);
  };
  const handleSaveAverageTonnage = () => {
    if (parseFloat(averageTonnage) > 0) {
      setUseAverageTonnage(true);
      setShowAverageTonnageInput(false);
      setShowScaleTicketModal(false);
      // Update current load with average tonnage
      setScaleTonnage(averageTonnage);
    } else {
      alert('Please enter a valid average tonnage value');
    }
  };
  const handleContinueWithScaleTicket = () => {
    // Check if tonnage is provided either directly or via average
    if (!scaleTonnage && !useAverageTonnage) {
      alert('Please enter tonnage information or set an average tonnage value before continuing');
      return;
    }
    // Continue with the load cycle using the captured scale ticket info
    setShowScaleTicketModal(false);
  };
  return <div className="min-h-screen bg-gray-100" data-id="element-2479">
      {/* Header */}
      <div className="bg-white shadow" data-id="element-2480">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2481">
          <div className="flex justify-between items-center py-4" data-id="element-2482">
            <div className="flex items-center" data-id="element-2483">
              <button onClick={onClose} className="mr-4 text-gray-500 hover:text-gray-700" data-id="element-2484">
                <ArrowLeftIcon size={24} data-id="element-2485" />
              </button>
              <h1 className="text-xl font-bold text-gray-900" data-id="element-2486">Job Tracking</h1>
            </div>
            <div className="flex space-x-2" data-id="element-2487">
              {showFinalizeButton && <button onClick={handleFinalizeJob} className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md flex items-center text-sm" data-id="element-2488">
                  <CheckIcon size={16} className="mr-1" data-id="element-2489" />
                  Finalize Job
                </button>}
              <button onClick={handleReportProblem} className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-md flex items-center text-sm" data-id="element-2490">
                <AlertCircleIcon size={16} className="mr-1" data-id="element-2491" />
                Report Problem
              </button>
              <button onClick={handleOpenCommentsModal} className="bg-purple-600 text-white px-3 py-2 rounded-md flex items-center text-sm" data-id="element-2492">
                <MessageSquareIcon size={16} className="mr-1" data-id="element-2493" />
                Comments
                {comments.length > 0 && <span className="ml-1 bg-white text-purple-600 rounded-full h-5 w-5 flex items-center justify-center text-xs font-bold" data-id="element-2494">
                    {comments.length}
                  </span>}
              </button>
              <button className="bg-gray-600 text-white px-3 py-2 rounded-md text-sm" data-id="element-2495">
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Job Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-2496">
        <div className="bg-white rounded-lg shadow p-4 mb-4" data-id="element-2497">
          <div className="flex justify-between items-center" data-id="element-2498">
            <h2 className="text-lg font-bold" data-id="element-2499">{job.jobName}</h2>
            <div className="flex space-x-2" data-id="element-2500">
              <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="foreman" buttonText="Message Foreman" className="bg-blue-100 text-blue-700 px-3 py-1 rounded-md flex items-center text-sm" data-id="element-2501" />
              <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="driver" buttonText="Message Drivers" className="bg-green-100 text-green-700 px-3 py-1 rounded-md flex items-center text-sm" data-id="element-2502" />
              <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="broker" buttonText="Message Broker" className="bg-purple-100 text-purple-700 px-3 py-1 rounded-md flex items-center text-sm" data-id="element-2503" />
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-md text-sm" data-id="element-2504">
                Assigned
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-2505">
          <div className="bg-blue-50 rounded-lg shadow p-4" data-id="element-2506">
            <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-2507">
              Total Loads
            </h3>
            <p className="text-3xl font-bold" data-id="element-2508">{totalLoads}</p>
          </div>
          <div className="bg-green-50 rounded-lg shadow p-4" data-id="element-2509">
            <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-2510">
              Total Tonnage
            </h3>
            <p className="text-3xl font-bold" data-id="element-2511">{totalTonnage.toFixed(2)}</p>
          </div>
          <div className="bg-purple-50 rounded-lg shadow p-4" data-id="element-2512">
            <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-2513">
              Total Hours
            </h3>
            <div className="flex items-center" data-id="element-2514">
              <ClockIcon size={20} className="text-purple-500 mr-2" data-id="element-2515" />
              <p className="text-3xl font-bold" data-id="element-2516">{totalHours.toFixed(2)}</p>
            </div>
            <p className="text-xs text-gray-500 mt-1" data-id="element-2517">
              Started: {startTime || 'Not started'}
            </p>
          </div>
        </div>

        {/* Geo Punch Tracking */}
        <div className="bg-white rounded-lg shadow mb-4" data-id="element-2518">
          <div className="flex justify-between items-center p-4 border-b" data-id="element-2519">
            <h3 className="font-bold" data-id="element-2520">Geo Punch Tracking</h3>
            <button onClick={() => setShowManualLoadModal(true)} className="bg-blue-600 text-white px-3 py-1 rounded-md flex items-center text-sm" data-id="element-2521">
              <PlusIcon size={16} className="mr-1" data-id="element-2522" />
              Add Load Manually
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 p-4" data-id="element-2523">
            {/* Load Location */}
            <div data-id="element-2524">
              <div className="flex items-center mb-2" data-id="element-2525">
                <MapPinIcon size={16} className="text-blue-600 mr-2" data-id="element-2526" />
                <h4 className="font-medium" data-id="element-2527">Load Location</h4>
              </div>
              <div className="mb-2" data-id="element-2528">
                <div className="flex justify-between items-center mb-1" data-id="element-2529">
                  <span className="text-sm text-gray-600" data-id="element-2530">Arrival:</span>
                  <span className="text-sm font-medium" data-id="element-2531">
                    {loadArrivalPunched ? currentLoad?.loadInTime : 'Not punched'}
                  </span>
                </div>
                <button onClick={handleArriveAtLoad} disabled={loadArrivalPunched} className={`w-full py-2 rounded-md flex items-center justify-center ${loadArrivalPunched ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-2532">
                  <ArrowRightIcon size={16} className="mr-2" data-id="element-2533" />
                  Arrive at Load
                </button>
              </div>
              <div data-id="element-2534">
                <div className="flex justify-between items-center mb-1" data-id="element-2535">
                  <span className="text-sm text-gray-600" data-id="element-2536">Departure:</span>
                  <span className="text-sm font-medium" data-id="element-2537">
                    {loadDeparturePunched ? currentLoad?.loadOutTime : 'Not punched'}
                  </span>
                </div>
                <button onClick={handleLeaveLoad} disabled={!loadArrivalPunched || loadDeparturePunched} className={`w-full py-2 rounded-md flex items-center justify-center ${!loadArrivalPunched || loadDeparturePunched ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`} data-id="element-2538">
                  <ArrowRightIcon size={16} className="mr-2" data-id="element-2539" />
                  Leave Load
                </button>
              </div>
            </div>
            {/* Dump Location */}
            <div data-id="element-2540">
              <div className="flex items-center mb-2" data-id="element-2541">
                <MapPinIcon size={16} className="text-red-600 mr-2" data-id="element-2542" />
                <h4 className="font-medium" data-id="element-2543">Dump Location</h4>
              </div>
              <div className="mb-2" data-id="element-2544">
                <div className="flex justify-between items-center mb-1" data-id="element-2545">
                  <span className="text-sm text-gray-600" data-id="element-2546">Arrival:</span>
                  <span className="text-sm font-medium" data-id="element-2547">
                    {dumpArrivalPunched ? currentLoad?.dumpInTime : 'Not punched'}
                  </span>
                </div>
                <button onClick={handleArriveAtDump} disabled={!loadDeparturePunched || dumpArrivalPunched} className={`w-full py-2 rounded-md flex items-center justify-center ${!loadDeparturePunched || dumpArrivalPunched ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`} data-id="element-2548">
                  <ArrowRightIcon size={16} className="mr-2" data-id="element-2549" />
                  Arrive at Dump
                </button>
              </div>
              <div data-id="element-2550">
                <div className="flex justify-between items-center mb-1" data-id="element-2551">
                  <span className="text-sm text-gray-600" data-id="element-2552">Departure:</span>
                  <span className="text-sm font-medium" data-id="element-2553">
                    {dumpDeparturePunched ? currentLoad?.dumpOutTime : 'Not punched'}
                  </span>
                </div>
                <button onClick={handleLeaveDump} disabled={!dumpArrivalPunched || dumpDeparturePunched} className={`w-full py-2 rounded-md flex items-center justify-center ${!dumpArrivalPunched || dumpDeparturePunched ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`} data-id="element-2554">
                  <ArrowRightIcon size={16} className="mr-2" data-id="element-2555" />
                  Leave Dump
                </button>
              </div>
            </div>
          </div>
          {/* Load Cycle Status */}
          <div className="p-4 border-t" data-id="element-2556">
            <h4 className="font-medium mb-3" data-id="element-2557">Load Cycle Status</h4>
            <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-2558">
              <div className="bg-gray-50 p-3 rounded-md" data-id="element-2559">
                <p className="text-xs text-center text-gray-500 mb-1" data-id="element-2560">
                  Load Arrival
                </p>
                <p className="text-center font-medium" data-id="element-2561">
                  {currentLoad?.loadInTime || '--'}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md" data-id="element-2562">
                <p className="text-xs text-center text-gray-500 mb-1" data-id="element-2563">
                  Load Departure
                </p>
                <p className="text-center font-medium" data-id="element-2564">
                  {currentLoad?.loadOutTime || '--'}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md" data-id="element-2565">
                <p className="text-xs text-center text-gray-500 mb-1" data-id="element-2566">
                  Dump Arrival
                </p>
                <p className="text-center font-medium" data-id="element-2567">
                  {currentLoad?.dumpInTime || '--'}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md" data-id="element-2568">
                <p className="text-xs text-center text-gray-500 mb-1" data-id="element-2569">
                  Dump Departure
                </p>
                <p className="text-center font-medium" data-id="element-2570">
                  {currentLoad?.dumpOutTime || '--'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Loads Table */}
        <div className="bg-white rounded-lg shadow mb-4" data-id="element-2571">
          <div className="p-4 border-b" data-id="element-2572">
            <h3 className="font-bold" data-id="element-2573">Loads</h3>
          </div>
          <div className="overflow-x-auto" data-id="element-2574">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2575">
              <thead className="bg-gray-50" data-id="element-2576">
                <tr data-id="element-2577">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2578">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2579">
                    Load In/Out
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2580">
                    Dump In/Out
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2581">
                    Scale Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2582">
                    Tons
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2583">
                    Total Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2584">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2585">
                {loads.length > 0 ? loads.map(load => <tr key={load.id} className={load.isManualEntry ? 'bg-red-50' : ''} data-id="element-2586">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2587">
                        {load.id}
                        {load.isManualEntry && <span className="ml-2 text-xs text-red-600 font-medium" data-id="element-2588">
                            (Manual Entry)
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2589">
                        {load.loadInTime} / {load.loadOutTime}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2590">
                        {load.dumpInTime} / {load.dumpOutTime}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2591">
                        {load.scaleTicketInfo?.ticketNumber || load.ticket}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2592">
                        {load.tons.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-2593">
                        {load.totalTime || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2594">
                        <div className="flex space-x-2" data-id="element-2595">
                          <button className="text-blue-600 hover:text-blue-800" onClick={() => {
                      // Show geo punch details in a dropdown
                      alert(`Geo Punch Details for Load #${load.id}:\n\n${load.isManualEntry ? 'MANUALLY ADDED LOAD - NO GEO PUNCH DATA' : `Load Arrival: ${load.geoPunchDetails?.loadArrival.time || 'N/A'}\nLoad Departure: ${load.geoPunchDetails?.loadDeparture.time || 'N/A'}\nDump Arrival: ${load.geoPunchDetails?.dumpArrival.time || 'N/A'}\nDump Departure: ${load.geoPunchDetails?.dumpDeparture.time || 'N/A'}`}`);
                    }} data-id="element-2596">
                            <div className="flex items-center" data-id="element-2597">
                              <TableIcon size={14} className="mr-1" data-id="element-2598" />
                              Details
                            </div>
                          </button>
                          {/* Show delete button only for manually added loads */}
                          {load.isManualEntry && <button className="text-red-600 hover:text-red-800" onClick={() => handleDeleteLoad(load.id)} data-id="element-2599">
                              <div className="flex items-center" data-id="element-2600">
                                <XIcon size={14} className="mr-1" data-id="element-2601" />
                                Delete
                              </div>
                            </button>}
                        </div>
                      </td>
                    </tr>) : <tr data-id="element-2602">
                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-2603">
                      No loads recorded yet. Click "Arrive at Load" to begin
                      tracking.
                    </td>
                  </tr>}
              </tbody>
              {loads.length > 0 && <tfoot className="bg-gray-50" data-id="element-2604">
                  <tr data-id="element-2605">
                    <td colSpan={4} className="px-6 py-3 text-right text-sm font-medium" data-id="element-2606">
                      Totals:
                    </td>
                    <td className="px-6 py-3 text-sm font-bold" data-id="element-2607">
                      {totalTonnage.toFixed(2)}
                    </td>
                    <td className="px-6 py-3 text-sm font-bold text-blue-600" data-id="element-2608">
                      {(() => {
                    // Calculate total minutes across all loads
                    const totalMinutes = loads.reduce((sum, load) => sum + (load.totalMinutes || 0), 0);
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    return `${hours}h ${minutes}m`;
                  })()}
                    </td>
                    <td data-id="element-2609"></td>
                  </tr>
                </tfoot>}
            </table>
          </div>
        </div>
        {/* Geo Punch Details Table */}
        {job.geoPunchRequired && loads.length > 0 && <div className="bg-white rounded-lg shadow mb-4" data-id="element-2610">
            <div className="p-4 border-b" data-id="element-2611">
              <h3 className="font-bold flex items-center" data-id="element-2612">
                <MapPinIcon size={18} className="mr-2 text-blue-600" data-id="element-2613" />
                Geo Punch Details
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-2614">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2615">
                <thead className="bg-gray-50" data-id="element-2616">
                  <tr data-id="element-2617">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2618">
                      Load #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2619">
                      Load Arrival
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2620">
                      Load Departure
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2621">
                      Dump Arrival
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2622">
                      Dump Departure
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2623">
                  {loads.map(load => <tr key={`geo-${load.id}`} data-id="element-2624">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2625">
                        {load.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2626">
                        {load.geoPunchDetails?.loadArrival.time || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2627">
                        {load.geoPunchDetails?.loadDeparture.time || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2628">
                        {load.geoPunchDetails?.dumpArrival.time || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2629">
                        {load.geoPunchDetails?.dumpDeparture.time || 'N/A'}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
        {/* Comments Section */}
        {comments.length > 0 && <div className="bg-white rounded-lg shadow mb-4" data-id="element-2630">
            <div className="p-4 border-b flex justify-between items-center" data-id="element-2631">
              <h3 className="font-bold flex items-center" data-id="element-2632">
                <MessageSquareIcon size={18} className="mr-2 text-purple-600" data-id="element-2633" />
                Driver Comments
              </h3>
              <button onClick={handleOpenCommentsModal} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2634">
                Add Comment
              </button>
            </div>
            <div className="p-4 space-y-3" data-id="element-2635">
              {comments.map(comment => <div key={comment.id} className="bg-gray-50 p-3 rounded-lg" data-id="element-2636">
                  <div className="flex justify-between items-start mb-1" data-id="element-2637">
                    <p className="font-medium" data-id="element-2638">Driver Comment</p>
                    <span className="text-xs text-gray-500" data-id="element-2639">
                      {comment.timestamp}
                    </span>
                  </div>
                  <p className="text-gray-700" data-id="element-2640">{comment.text}</p>
                </div>)}
            </div>
          </div>}
      </div>

      {/* Comments Modal */}
      {showCommentsModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2641">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 shadow-xl" data-id="element-2642">
            <div className="p-4 border-b flex justify-between items-center" data-id="element-2643">
              <h3 className="font-bold" data-id="element-2644">Add Comment to Timecard</h3>
              <button onClick={handleCloseCommentsModal} className="text-gray-500 hover:text-gray-700" data-id="element-2645">
                <XIcon size={20} data-id="element-2646" />
              </button>
            </div>
            <div className="p-4" data-id="element-2647">
              <p className="text-sm text-gray-600 mb-2" data-id="element-2648">
                Add any comments about this job that should be included with the
                final timecard.
              </p>
              <textarea value={newComment} onChange={e => setNewComment(e.target.value)} className="w-full border rounded-md p-2 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Enter your comments here..." data-id="element-2649" />
              <div className="flex justify-end space-x-2 mt-4" data-id="element-2650">
                <button onClick={handleCloseCommentsModal} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2651">
                  Cancel
                </button>
                <button onClick={handleSaveComment} disabled={!newComment.trim()} className={`px-4 py-2 rounded-md text-white ${newComment.trim() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`} data-id="element-2652">
                  Save Comment
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Scale Ticket Modal */}
      {showScaleTicketModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2653">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 shadow-xl" data-id="element-2654">
            <div className="p-4 border-b flex justify-between items-center" data-id="element-2655">
              <h3 className="font-bold" data-id="element-2656">Scale Ticket Information</h3>
              <button onClick={() => setShowAverageTonnageInput(true)} className="text-gray-500 hover:text-gray-700" data-id="element-2657">
                <XIcon size={20} data-id="element-2658" />
              </button>
            </div>
            <div className="p-4" data-id="element-2659">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4" data-id="element-2660">
                <p className="text-sm text-gray-700" data-id="element-2661">
                  You've left the loading location. Please enter the scale
                  ticket information.
                </p>
                <div className="mt-2 text-xs text-blue-800 bg-blue-50 p-2 rounded border border-blue-100" data-id="element-2662">
                  <p className="font-medium" data-id="element-2663">Required:</p>
                  <p data-id="element-2664">
                    You must provide tonnage information before continuing.
                    Either enter the tonnage directly or set an average tonnage
                    value.
                  </p>
                </div>
              </div>
              <div className="mb-4" data-id="element-2665">
                <p className="text-sm font-medium mb-2" data-id="element-2666">
                  Pro Tip: You can both upload a photo AND manually enter ticket
                  details. If you scan your scale ticket with AI, it will
                  auto-fill the fields below.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 mb-4" data-id="element-2667">
                  <button onClick={handleTakePhoto} className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2 hover:bg-blue-700" data-id="element-2668">
                    <CameraIcon size={18} data-id="element-2669" />
                    Take Photo
                  </button>
                  <button onClick={handleUploadImage} className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2 hover:bg-green-700" data-id="element-2670">
                    <UploadIcon size={18} data-id="element-2671" />
                    Upload Image
                  </button>
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} data-id="element-2672" />
                </div>
              </div>
              {scaleTicketImage && <div className="mb-4" data-id="element-2673">
                  <p className="text-sm font-medium mb-2" data-id="element-2674">Captured Image:</p>
                  <div className="border rounded-md overflow-hidden" data-id="element-2675">
                    <img src={scaleTicketImage} alt="Scale Ticket" className="w-full h-auto max-h-40 object-contain" data-id="element-2676" />
                  </div>
                </div>}
              <div className="mb-4" data-id="element-2677">
                <h4 className="text-sm font-medium mb-2" data-id="element-2678">
                  Scale Ticket Details
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-2679">
                  <div data-id="element-2680">
                    <label className="block text-sm text-gray-600 mb-1" data-id="element-2681">
                      Ticket Number (Optional)
                    </label>
                    <input type="text" value={scaleTicketNumber} onChange={e => setScaleTicketNumber(e.target.value)} className="w-full border border-gray-300 rounded-md p-2" placeholder="Enter ticket number" data-id="element-2682" />
                  </div>
                  <div data-id="element-2683">
                    <label className="block text-sm text-gray-600 mb-1" data-id="element-2684">
                      Tons <span className="text-red-500" data-id="element-2685">*</span>
                    </label>
                    <input type="number" value={scaleTonnage} onChange={e => setScaleTonnage(e.target.value)} className="w-full border border-gray-300 rounded-md p-2" placeholder="Enter tonnage" data-id="element-2686" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-2687">
                      Required unless using average tonnage
                    </p>
                  </div>
                </div>
              </div>
              {showAverageTonnageInput ? <div className="mb-4 bg-blue-50 p-4 rounded-md border border-blue-200" data-id="element-2688">
                  <h4 className="text-sm font-medium mb-2" data-id="element-2689">
                    Set Average Tonnage for All Loads
                  </h4>
                  <p className="text-xs text-gray-600 mb-2" data-id="element-2690">
                    This value will be used for all loads in this job when
                    specific tonnage is not provided.
                  </p>
                  <div className="flex items-center gap-2" data-id="element-2691">
                    <input type="number" value={averageTonnage} onChange={e => setAverageTonnage(e.target.value)} className="flex-1 border border-gray-300 rounded-md p-2" placeholder="Enter average tonnage" data-id="element-2692" />
                    <button onClick={handleSaveAverageTonnage} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700" data-id="element-2693">
                      Save
                    </button>
                  </div>
                </div> : <div className="flex justify-center mb-4" data-id="element-2694">
                  <button onClick={handleSetAverageTonnage} className="flex items-center gap-1 text-blue-600 hover:text-blue-800" data-id="element-2695">
                    <InfoIcon size={16} data-id="element-2696" />
                    <span className="text-sm" data-id="element-2697">Use Average Tonnage Instead</span>
                  </button>
                </div>}
              <div className="flex justify-end mt-4" data-id="element-2698">
                <button onClick={handleContinueWithScaleTicket} disabled={!scaleTonnage && !useAverageTonnage} className={`bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 flex items-center gap-2 ${!scaleTonnage && !useAverageTonnage ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-2699">
                  <CheckCircleIcon size={18} data-id="element-2700" />
                  Continue with Load Cycle
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Manual Load Entry Modal */}
      {showManualLoadModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2701">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 shadow-xl" data-id="element-2702">
            <div className="p-4 border-b flex justify-between items-center" data-id="element-2703">
              <h3 className="font-bold" data-id="element-2704">Add New Load</h3>
              <button onClick={() => setShowManualLoadModal(false)} className="text-gray-500 hover:text-gray-700" data-id="element-2705">
                <XIcon size={20} data-id="element-2706" />
              </button>
            </div>
            <div className="p-4" data-id="element-2707">
              <div className="bg-blue-50 p-3 rounded-md mb-4" data-id="element-2708">
                <p className="text-sm text-blue-800" data-id="element-2709">
                  Note: Use this form to manually add a load that wasn't
                  captured through geo punch tracking. Ticket number and tonnage
                  are optional but recommended.
                </p>
              </div>
              {/* Scale Ticket Image */}
              <div className="mb-4" data-id="element-2710">
                <p className="text-sm font-medium mb-2" data-id="element-2711">
                  Scale Ticket Image (Optional)
                </p>
                <div className="grid grid-cols-2 gap-3" data-id="element-2712">
                  <button onClick={() => {
                // Simulate camera capture
                setTimeout(() => {
                  handleManualScaleTicketImage('https://uploadthingy.s3.us-west-1.amazonaws.com/jdumz44utk289BfoNoFRwx/driver_scale_ticket_manual_add.jpg');
                }, 1000);
              }} className="bg-blue-600 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2" data-id="element-2713">
                    <CameraIcon size={18} data-id="element-2714" />
                    Take Photo
                  </button>
                  <button onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }} className="bg-blue-600 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2" data-id="element-2715">
                    <UploadIcon size={18} data-id="element-2716" />
                    Upload Image
                  </button>
                </div>
                {manualLoad.scaleTicketImage && <div className="mt-3 border rounded-md overflow-hidden" data-id="element-2717">
                    <img src={manualLoad.scaleTicketImage} alt="Scale Ticket" className="w-full h-auto max-h-40 object-contain" data-id="element-2718" />
                  </div>}
              </div>
              {/* Ticket Number and Tons */}
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-2719">
                <div data-id="element-2720">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-2721">
                    Ticket Number (Optional)
                  </label>
                  <input type="text" value={manualLoad.ticketNumber} onChange={e => handleManualLoadChange('ticketNumber', e.target.value)} className="w-full border border-gray-300 rounded-md p-2" placeholder="Enter ticket number" data-id="element-2722" />
                </div>
                <div data-id="element-2723">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-2724">
                    Tons (Optional)
                  </label>
                  <input type="number" value={manualLoad.tons} onChange={e => handleManualLoadChange('tons', e.target.value)} className="w-full border border-gray-300 rounded-md p-2" placeholder="Enter tonnage" data-id="element-2725" />
                </div>
              </div>
              {/* Load Times */}
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-2726">
                <div data-id="element-2727">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-2728">
                    Load In Time
                  </label>
                  <input type="text" value={manualLoad.loadInTime} onChange={e => handleManualLoadChange('loadInTime', e.target.value)} className="w-full border border-gray-300 rounded-md p-2" placeholder="e.g. 9:30 AM or 14:30" data-id="element-2729" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2730">
                    Format: HH:MM or HH:MM AM/PM
                  </p>
                </div>
                <div data-id="element-2731">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-2732">
                    Load Out Time
                  </label>
                  <input type="text" value={manualLoad.loadOutTime} onChange={e => handleManualLoadChange('loadOutTime', e.target.value)} className="w-full border border-gray-300 rounded-md p-2" placeholder="e.g. 9:45 AM or 14:45" data-id="element-2733" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2734">
                    Format: HH:MM or HH:MM AM/PM
                  </p>
                </div>
              </div>
              {/* Dump Times */}
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-2735">
                <div data-id="element-2736">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-2737">
                    Dump In Time
                  </label>
                  <input type="text" value={manualLoad.dumpInTime} onChange={e => handleManualLoadChange('dumpInTime', e.target.value)} className="w-full border border-gray-300 rounded-md p-2" placeholder="e.g. 10:30 AM or 15:30" data-id="element-2738" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2739">
                    Format: HH:MM or HH:MM AM/PM
                  </p>
                </div>
                <div data-id="element-2740">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-2741">
                    Dump Out Time
                  </label>
                  <input type="text" value={manualLoad.dumpOutTime} onChange={e => handleManualLoadChange('dumpOutTime', e.target.value)} className="w-full border border-gray-300 rounded-md p-2" placeholder="e.g. 10:45 AM or 15:45" data-id="element-2742" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2743">
                    Format: HH:MM or HH:MM AM/PM
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end" data-id="element-2744">
              <button onClick={handleManualLoadSubmit} className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-2745">
                <CheckCircleIcon size={18} data-id="element-2746" />
                Save Load
              </button>
            </div>
          </div>
        </div>}

      {/* Report Problem Modal */}
      {showReportProblemModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2747">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 shadow-xl" data-id="element-2748">
            <div className="p-4 border-b flex justify-between items-center bg-orange-50" data-id="element-2749">
              <h3 className="font-bold flex items-center gap-2 text-orange-800" data-id="element-2750">
                <AlertCircleIcon size={20} className="text-orange-600" data-id="element-2751" />
                Report Problem
              </h3>
              <button onClick={handleCloseReportProblem} className="text-gray-500 hover:text-gray-700" data-id="element-2752">
                <XIcon size={20} data-id="element-2753" />
              </button>
            </div>
            <div className="p-4" data-id="element-2754">
              <div className="mb-4" data-id="element-2755">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2756">
                  Problem Type <span className="text-red-500" data-id="element-2757">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-2758">
                  <button onClick={() => setProblemType('late')} className={`p-3 border rounded-md flex flex-col items-center gap-1 transition-colors ${problemType === 'late' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2759">
                    <ClockIcon size={24} className={problemType === 'late' ? 'text-orange-500' : 'text-gray-400'} data-id="element-2760" />
                    <span className="text-sm font-medium" data-id="element-2761">Running Late</span>
                  </button>
                  <button onClick={() => setProblemType('breakdown')} className={`p-3 border rounded-md flex flex-col items-center gap-1 transition-colors ${problemType === 'breakdown' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2762">
                    <TruckIcon size={24} className={problemType === 'breakdown' ? 'text-orange-500' : 'text-gray-400'} data-id="element-2763" />
                    <span className="text-sm font-medium" data-id="element-2764">Truck Down</span>
                  </button>
                  <button onClick={() => setProblemType('emergency')} className={`p-3 border rounded-md flex flex-col items-center gap-1 transition-colors ${problemType === 'emergency' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2765">
                    <AlertTriangleIcon size={24} className={problemType === 'emergency' ? 'text-red-500' : 'text-gray-400'} data-id="element-2766" />
                    <span className="text-sm font-medium" data-id="element-2767">Emergency</span>
                  </button>
                </div>
                {problemType === 'emergency' && <p className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded border border-red-100" data-id="element-2768">
                    <strong data-id="element-2769">Note:</strong> Emergency alerts are for serious
                    situations requiring immediate attention. This will trigger
                    high-priority notifications.
                  </p>}
              </div>
              <div className="mb-4" data-id="element-2770">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2771">
                  Details <span className="text-red-500" data-id="element-2772">*</span>
                </label>
                <textarea value={problemComment} onChange={e => setProblemComment(e.target.value)} className="w-full border border-gray-300 rounded-md p-3 min-h-[100px] focus:ring-blue-500 focus:border-blue-500" placeholder={problemType === 'late' ? "Explain why you're running late and provide an estimated arrival time..." : problemType === 'breakdown' ? 'Describe the truck issue and your location...' : problemType === 'emergency' ? 'Describe the emergency situation and any assistance needed...' : 'Provide details about the problem...'} data-id="element-2773"></textarea>
              </div>
              <div className="mb-4" data-id="element-2774">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2775">
                  Notify
                </label>
                <div className="space-y-2" data-id="element-2776">
                  <div className="flex items-center" data-id="element-2777">
                    <input type="checkbox" id="notify-all" checked={notifyRecipients.all} onChange={e => handleNotifyAll(e.target.checked)} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2778" />
                    <label htmlFor="notify-all" className="ml-2 text-sm font-medium text-gray-700" data-id="element-2779">
                      Notify all parties
                    </label>
                  </div>
                  {!notifyRecipients.all && <div className="grid grid-cols-3 gap-2 mt-2" data-id="element-2780">
                      <div className="flex items-center" data-id="element-2781">
                        <input type="checkbox" id="notify-trucking" checked={notifyRecipients.trucking} onChange={e => setNotifyRecipients({
                    ...notifyRecipients,
                    trucking: e.target.checked
                  })} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2782" />
                        <label htmlFor="notify-trucking" className="ml-2 text-sm text-gray-700" data-id="element-2783">
                          Trucking Company
                        </label>
                      </div>
                      <div className="flex items-center" data-id="element-2784">
                        <input type="checkbox" id="notify-broker" checked={notifyRecipients.broker} onChange={e => setNotifyRecipients({
                    ...notifyRecipients,
                    broker: e.target.checked
                  })} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2785" />
                        <label htmlFor="notify-broker" className="ml-2 text-sm text-gray-700" data-id="element-2786">
                          Broker
                        </label>
                      </div>
                      <div className="flex items-center" data-id="element-2787">
                        <input type="checkbox" id="notify-foreman" checked={notifyRecipients.foreman} onChange={e => setNotifyRecipients({
                    ...notifyRecipients,
                    foreman: e.target.checked
                  })} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2788" />
                        <label htmlFor="notify-foreman" className="ml-2 text-sm text-gray-700" data-id="element-2789">
                          Foreman
                        </label>
                      </div>
                    </div>}
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2" data-id="element-2790">
              <button onClick={handleCloseReportProblem} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100" data-id="element-2791">
                Cancel
              </button>
              <button onClick={handleSubmitProblemReport} disabled={!problemType || !problemComment} className={`px-4 py-2 rounded-md text-white flex items-center gap-2 ${problemType === 'emergency' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'} ${!problemType || !problemComment ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-2792">
                <AlertCircleIcon size={16} data-id="element-2793" />
                Send Alert
              </button>
            </div>
          </div>
        </div>}

      {/* Job Completion Modal */}
      {showCompletionModal && <JobCompletionModal jobId={job.id} onComplete={handleCompleteJob} onCancel={handleCancelCompletion} data-id="element-2794" />}
    </div>;
};